import React from "react";
import { motion } from "framer-motion";
import {
    PencilRuler,
    Package,
    Building2,
    Compass,
    Layers,
    Handshake,
    ShieldCheck,
    Building,
    Wrench,
    HardHat,
    Warehouse,
    Home as HomeIcon,
} from "lucide-react";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { Button } from "../components/zen/Button";
import { DivisionCard } from "../components/zen/DivisionCard";
import { MetricStat } from "../components/zen/MetricStat";
import { AnimatedGrid } from "../components/zen/AnimatedGrid";

const fade = {
    hidden: { opacity: 0, y: 16 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            delay: i * 0.08,
            ease: [0.16, 1, 0.3, 1],
        },
    }),
};

const TRUST_MARKERS = [
    "Multi-Industry Portfolio",
    "US-Managed Delivery",
    "Long-Term Partnerships",
    "Multidisciplinary Execution",
];

const DIVISIONS = [
    {
        icon: PencilRuler,
        title: "BIM & Engineering Services",
        tagline: "Engineering",
        description:
            "Multidisciplinary BIM, drafting, and engineering production support for U.S. engineering firms, contractors, and architects.",
        cta: "Explore Services",
        href: "/bim",
    },
    {
        icon: Package,
        logoSrc: "/logos/citywood.png",
        logoAlt: "CityWood — wholesale timber supply",
        logoPad: "p-1",
        title: "Building Materials",
        tagline: "Industrial Supply",
        description:
            "Wholesale rough pine, hardwood, timber, and industrial materials sourced and delivered at scale.",
        cta: "Request Pricing",
        href: "/building-materials",
    },
    {
        icon: Building2,
        title: "Real Estate Development",
        tagline: "Real Assets",
        description:
            "Off-market acquisitions and development of residential and commercial properties across U.S. markets.",
        cta: "Submit Property",
        href: "/real-estate",
    },
    {
        icon: Compass,
        logoSrc: "/logos/zenme-z-mark.png",
        logoAlt: "ZenMe Group corporate mark",
        title: "Business Consulting",
        tagline: "Advisory",
        description:
            "Operational strategy, growth advisory, and organizational design for founders, owners, and operators.",
        cta: "Schedule Consultation",
        href: "/consulting",
    },
];

const POSITIONING = [
    {
        icon: Layers,
        title: "Multi-industry expertise",
        description:
            "Operating across engineering, infrastructure, real estate, and consulting — with a unified discipline.",
    },
    {
        icon: Handshake,
        title: "Direct partnerships",
        description:
            "We work directly with founders, owners, and operators — no intermediaries, no friction.",
    },
    {
        icon: ShieldCheck,
        title: "Dependable execution",
        description:
            "Patient, structured delivery focused on repeat client relationships and long-term outcomes.",
    },
];

const METRICS = [
    { value: "4", label: "Active Divisions" },
    { value: "16+", label: "Engineering Disciplines" },
    { value: "12+", label: "Industries Served" },
    { value: "24h", label: "Response Time" },
];

const INDUSTRY_TAGS = [
    { label: "Engineering Services", icon: PencilRuler },
    { label: "Industrial Supply", icon: Warehouse },
    { label: "Real Estate", icon: HomeIcon },
    { label: "Construction Support", icon: HardHat },
    { label: "Advisory & Strategy", icon: Wrench },
];

export default function Home() {
    return (
        <PageShell>
            {/* HERO */}
            <section className="relative isolate min-h-[100svh] overflow-hidden pt-[88px]">
                <AnimatedGrid />
                <Container className="relative z-10 flex min-h-[calc(100svh-88px)] flex-col justify-center py-20">
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={{ show: { transition: { staggerChildren: 0.08 } } }}
                        className="max-w-[920px]"
                    >
                        <motion.div variants={fade} custom={0}>
                            <span
                                className="inline-flex items-center gap-2 rounded-full border border-border-subtle bg-[rgba(255,255,255,0.02)] px-3.5 py-1.5 backdrop-blur-md"
                                data-testid="hero-eyebrow"
                            >
                                <span
                                    aria-hidden
                                    className="h-1.5 w-1.5 rounded-full bg-[var(--accent)] animate-pulse-dot"
                                    style={{
                                        boxShadow: "0 0 12px rgba(124,255,79,0.7)",
                                    }}
                                />
                                <span className="text-[11px] uppercase tracking-widest-2 text-text-secondary">
                                    ZenMe Group LLC — Holding Company
                                </span>
                            </span>
                        </motion.div>

                        <motion.h1
                            variants={fade}
                            custom={1}
                            className="mt-8 font-display font-semibold tracking-tight-2 leading-[1.02] text-text-primary text-[clamp(44px,7.6vw,104px)]"
                            data-testid="hero-headline"
                        >
                            Engineering.
                            <br />
                            Infrastructure.
                            <br />
                            <span className="text-text-primary">
                                Execution.
                            </span>
                        </motion.h1>

                        <motion.p
                            variants={fade}
                            custom={2}
                            className="mt-8 max-w-[640px] text-[17px] leading-[1.65] text-text-secondary"
                        >
                            ZenMe Group LLC operates a portfolio of businesses
                            across engineering services, industrial supply,
                            real estate, and strategic consulting — helping
                            companies scale through specialized expertise and
                            dependable execution.
                        </motion.p>

                        <motion.div
                            variants={fade}
                            custom={3}
                            className="mt-10 flex flex-wrap items-center gap-3"
                        >
                            <Button
                                to="/divisions"
                                variant="primary"
                                size="lg"
                                data-testid="hero-cta-explore"
                            >
                                Explore Divisions
                            </Button>
                            <Button
                                to="/contact"
                                variant="ghost"
                                size="lg"
                                data-testid="hero-cta-contact"
                            >
                                Contact Us
                            </Button>
                        </motion.div>

                        <motion.div
                            variants={fade}
                            custom={4}
                            className="mt-14 grid grid-cols-2 gap-x-6 gap-y-3 sm:flex sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-3"
                        >
                            {TRUST_MARKERS.map((m) => (
                                <span
                                    key={m}
                                    className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest-2 text-text-muted"
                                >
                                    <span
                                        aria-hidden
                                        className="h-1 w-1 rounded-full bg-[var(--accent)]"
                                    />
                                    {m}
                                </span>
                            ))}
                        </motion.div>
                    </motion.div>
                </Container>
            </section>

            {/* DIVISIONS — 2x2 grid, 4 cards */}
            <Section id="divisions" className="border-t border-border-subtle">
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-2xl"
                    >
                        <Eyebrow>Our Portfolio</Eyebrow>
                        <Heading as="h2" className="mt-4">
                            Four divisions. One platform.
                        </Heading>
                        <p className="mt-5 text-[17px] leading-[1.65] text-text-secondary">
                            A focused operating group across engineering,
                            infrastructure, and real assets — each division
                            built for scale and run with discipline.
                        </p>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={{ show: { transition: { staggerChildren: 0.08 } } }}
                        className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8"
                    >
                        {DIVISIONS.map((d, i) => (
                            <motion.div
                                key={d.title}
                                variants={fade}
                                custom={i}
                                className="h-full"
                            >
                                <DivisionCard
                                    {...d}
                                    className="h-full"
                                    data-testid={`division-card-${i}`}
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                </Container>
            </Section>

            {/* WHY ZENME GROUP */}
            <Section className="border-t border-border-subtle">
                <Container>
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{
                                duration: 0.7,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="lg:col-span-5"
                        >
                            <Eyebrow>Positioning</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Built for serious operators.
                            </Heading>
                            <p className="mt-5 max-w-md text-[17px] leading-[1.65] text-text-secondary">
                                We invest, operate, and partner with
                                discipline — across cycles, industries, and
                                delivery models.
                            </p>
                        </motion.div>

                        <div className="lg:col-span-7 grid grid-cols-1 gap-12 sm:grid-cols-3">
                            {POSITIONING.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{
                                        once: true,
                                        margin: "-80px",
                                    }}
                                    transition={{
                                        duration: 0.7,
                                        delay: i * 0.08,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border-subtle bg-[var(--bg-elevated)]">
                                        <p.icon
                                            className="h-4 w-4 text-[var(--accent)]"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                    <h3 className="mt-6 font-display text-[20px] font-semibold leading-tight text-text-primary">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-[14.5px] leading-[1.65] text-text-secondary">
                                        {p.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </Container>
            </Section>

            {/* METRICS */}
            <section
                className="relative border-y border-border-subtle bg-elevated"
                data-testid="metrics-strip"
            >
                <Container>
                    <div className="py-16 lg:py-20">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{
                                duration: 0.7,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="mb-10"
                        >
                            <Eyebrow withDot>By the Numbers</Eyebrow>
                        </motion.div>
                        <div className="grid grid-cols-2 gap-y-12 gap-x-6 lg:grid-cols-4 lg:gap-x-10">
                            {METRICS.map((m, i) => (
                                <MetricStat
                                    key={m.label}
                                    value={m.value}
                                    label={m.label}
                                    data-testid={`metric-${i}`}
                                />
                            ))}
                        </div>
                    </div>
                </Container>
            </section>

            {/* INDUSTRIES STRIP */}
            <Section className="border-b border-border-subtle">
                <Container>
                    <Eyebrow>Trusted Across Industries</Eyebrow>
                    <div className="mt-8 grid grid-cols-2 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
                        {INDUSTRY_TAGS.map(({ label, icon: Icon }) => (
                            <div
                                key={label}
                                className="flex items-center gap-2.5 text-[13px] uppercase tracking-widest-2 text-text-muted"
                            >
                                <Icon
                                    className="h-3.5 w-3.5 opacity-50"
                                    strokeWidth={1.5}
                                />
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                </Container>
            </Section>

            {/* CLOSING CTA */}
            <section className="relative isolate overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <div
                        className="absolute left-1/2 top-1/2 h-[600px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                        style={{
                            background:
                                "radial-gradient(ellipse at center, rgba(124,255,79,0.10) 0%, rgba(124,255,79,0.03) 35%, transparent 65%)",
                            filter: "blur(60px)",
                        }}
                    />
                    <div className="zen-grid opacity-50" />
                </div>
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="mx-auto max-w-3xl py-32 text-center lg:py-40"
                    >
                        <Eyebrow withDot>Let's Talk</Eyebrow>
                        <Heading as="h2" className="mt-6">
                            Let's build something enduring.
                        </Heading>
                        <p className="mx-auto mt-6 max-w-xl text-[17px] leading-[1.65] text-text-secondary">
                            Whether you're expanding your engineering team,
                            seeking partnership, or exploring strategic
                            advisory — we're ready to talk.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                            <Button
                                to="/contact"
                                variant="primary"
                                size="lg"
                                data-testid="closing-cta-contact"
                            >
                                Contact Us
                            </Button>
                            <Button
                                to="/divisions"
                                variant="ghost"
                                size="lg"
                                data-testid="closing-cta-divisions"
                            >
                                Explore Divisions
                            </Button>
                        </div>
                    </motion.div>
                </Container>
            </section>
        </PageShell>
    );
}
